import React, { useState } from "react";
import "../styles/user_management_styles.css";
import Navbar from "./Navbar.js";
import "bootstrap/dist/css/bootstrap.min.css";
import useFetch from "./useFetch.js";
import swal from "sweetalert";
import axios from "axios";
import MultiSelect from "./MultiSelect.js";
import config from "../config.js";
import Cookie from "universal-cookie";
import mixpanel from "mixpanel-browser";
import { Trash2, AlertTriangle, Layers } from "lucide-react";
import { Box, Typography, Button, Card, CircularProgress } from "@mui/material";

const Views_Delete = () => {
  const cookies = new Cookie();
  let userToken = "";
  let actionInitiatingUser = -1;
  try {
    userToken = cookies.get("userToken");
    actionInitiatingUser = parseInt(cookies.get("userID"));
  } catch (error) {}

  const headers = {
    Authorization: `Bearer ${userToken}`,
    "Content-Type": "application/json",
  };

  const {
    data: views,
    isPending: isPendingViews,
    error: errorViews,
  } = useFetch({
    actionInitiatingUser: { userID: actionInitiatingUser.toString() },
    URL: config.BACKEND_URL + "/views",
  });

  let selectedViews = [];

  const handleDelete = (e) => {
    e.preventDefault();
    mixpanel.track("View Delete Button Clicked", {
      userID: cookies.get("userID"),
    });
    if (selectedViews.length === 0) {
      swal(
        "View not selected!",
        "Select a view before it can be deleted.",
        "error"
      );
    } else {
      swal({
        title: `Are you sure?`,
        text: "Are you sure you want to delete the selected views?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then(async (willDelete) => {
        if (willDelete) {
          for (let id of selectedViews) {
            try {
              await axios.delete(
                config.BACKEND_URL + `/views/${id}/${actionInitiatingUser}`,
                { headers }
              );
            } catch (err) {
              console.log(err);
            }
          }
          swal(
            "Views deleted successfully!",
            "Click on OK to continue...",
            "success"
          ).then(() => {
            window.location.reload();
          });
        } else {
          swal(
            "Cancelled!",
            "The selected view has not been deleted!",
            "error"
          );
        }
      });
    }
  };

  const handleViewsSelections = (selected) => {
    selectedViews = selected.map((view) => view.value);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, #1e293b 0%, #020617 45%, #020617 100%)",
        color: "white",
      }}
    >
      <Navbar />

      <main className="container py-5 mt-4">
        <Box sx={{ maxWidth: "800px", mx: "auto" }}>
          {/* Header Section */}
          <Box sx={{ textAlign: "center", mb: 5 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Trash2 size={40} color="#ef4444" /> Delete Views
            </Typography>
            <Typography
              sx={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.7)" }}
            >
              Select the views you wish to permanently remove from the system.
            </Typography>
          </Box>

          {/* Main Action Card */}
          <Card
            sx={{
              background: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(12px)",
              borderRadius: "24px",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              p: 4,
            }}
          >
            <form id="deleteForm" onSubmit={handleDelete}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  sx={{
                    color: "#94a3b8",
                    fontWeight: 600,
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Layers size={18} /> SELECT VIEWS
                </Typography>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    background: "rgba(2, 6, 23, 0.4)",
                    border: "1px solid rgba(255,255,255,0.05)",
                    minHeight: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {isPendingViews && (
                    <Box sx={{ textAlign: "center" }}>
                      <CircularProgress
                        size={30}
                        sx={{ color: "#3b82f6", mb: 1 }}
                      />
                      <Typography variant="body2">Loading views...</Typography>
                    </Box>
                  )}

                  {errorViews && (
                    <Typography
                      color="error"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <AlertTriangle size={18} /> Cannot fetch views at this
                      time.
                    </Typography>
                  )}

                  {!isPendingViews && views && views.length > 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        "& .css-b62m3t-container": { color: "black" },
                      }}
                    >
                      <MultiSelect
                        handleChange={handleViewsSelections}
                        data={views.map((view) => ({
                          value: view.id,
                          label: view.name,
                        }))}
                        defaultSelected={[]}
                      />
                    </Box>
                  )}

                  {!isPendingViews && views && views.length === 0 && (
                    <Typography sx={{ color: "#64748b" }}>
                      Currently, there are no views available.
                    </Typography>
                  )}
                </Box>
              </Box>

              {views && views.length > 0 && (
                <Box sx={{ textAlign: "center" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      bgcolor: "#ef4444",
                      color: "white",
                      px: 6,
                      py: 1.5,
                      borderRadius: "12px",
                      fontWeight: 700,
                      fontSize: "1rem",
                      textTransform: "none",
                      boxShadow: "0 10px 20px rgba(70, 70, 70, 0.3)",
                      "&:hover": {
                        bgcolor: "#505050ff",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    Delete Selected Views
                  </Button>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 3,
                      color: "rgba(255,255,255,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <AlertTriangle size={14} /> This action cannot be undone.
                  </Typography>
                </Box>
              )}
            </form>
          </Card>
        </Box>
      </main>
    </Box>
  );
};

export default Views_Delete;
